import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LANGUAGES,
  activeLanguage,
  getLanguage,
  groupedLanguages,
  switchLanguage,
} from '../config/languages';

// Show the search box once the list is long enough to need it.
const SEARCH_THRESHOLD = 8;

const BUTTON_CLASSES = {
  app: 'flex items-center gap-1 p-2 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition',
  // Compact on phones: the landing nav also holds the logo and the login button.
  landing: 'flex items-center gap-1.5 sm:gap-2 text-gray-700 dark:text-gray-300 hover:text-blue-600 font-medium transition duration-300 px-2 sm:px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-800',
};

/**
 * Language switcher shared by the app header and the public pages (landing,
 * module detail). Languages are grouped by region (config/languages.js) and
 * shown under their native names with their code; a search box filters them.
 * Languages whose translation is in progress carry a "beta" badge.
 *
 * The panel is [data-i18n-ignore]: it lists every language in its own script
 * on purpose. Each name is a <bdi lang>, so screen readers and the browser's
 * font selection treat "العربية" as Arabic even in a Turkish UI.
 */
const LanguageSwitcher = ({ variant = 'app' }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const buttonRef = useRef(null);

  // Always a supported base code ("en" for an en-US browser), also for a
  // language whose locale file is still empty (see activeLanguage).
  const current = getLanguage(activeLanguage(i18n));
  const groups = useMemo(() => groupedLanguages(query), [query]);
  const showSearch = LANGUAGES.length > SEARCH_THRESHOLD;

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        if (buttonRef.current) buttonRef.current.focus();
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    // Focus the search box for mouse / keyboard users; on touch screens it
    // would pop up the on-screen keyboard over the list.
    const pointer = typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: fine)') : null;
    const finePointer = Boolean(pointer && pointer.matches);
    if (finePointer && searchRef.current) searchRef.current.focus();
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const choose = (code) => {
    switchLanguage(i18n, code);
    setOpen(false);
  };

  // Enter in the search box picks the first match.
  const onSearchKey = (e) => {
    if (e.key === 'Enter' && groups.length) {
      e.preventDefault();
      choose(groups[0].languages[0].code);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        data-testid="language-switcher"
        data-lang={current.code}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('shell.layout.changeLanguage')}
        title={current.label}
        onClick={() => setOpen((o) => !o)}
        className={BUTTON_CLASSES[variant] || BUTTON_CLASSES.app}
      >
        {/* Globe + code rather than a flag: languages are not countries, and
            Windows has no flag emoji (it would print "TR TR"). */}
        <svg className={variant === 'landing' ? 'w-5 h-5' : 'w-4 h-4'} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" strokeWidth={1.75} />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
        </svg>
        <span className={variant === 'landing' ? 'text-sm' : 'text-xs font-semibold uppercase'}>{current.short}</span>
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''} ${variant === 'landing' ? 'hidden sm:block' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          data-i18n-ignore
          data-testid="language-menu"
          role="dialog"
          aria-label={t('shell.layout.changeLanguage')}
          // Phones: a full-width sheet under the 4rem header (an anchored
          // 18rem panel would overflow the screen, in RTL too). sm+: dropdown.
          className="fixed inset-x-3 top-16 sm:absolute sm:inset-x-auto sm:top-auto sm:end-0 sm:mt-2 sm:w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 flex flex-col max-h-[75vh]"
        >
          {showSearch && (
            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
              <input
                ref={searchRef}
                type="search"
                data-testid="language-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKey}
                placeholder={t('shell.languageSwitcher.search')}
                aria-label={t('shell.languageSwitcher.search')}
                autoComplete="off"
                className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}
          <div className="overflow-y-auto overscroll-contain py-1">
            {groups.length === 0 && (
              <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{t('shell.languageSwitcher.noResults')}</p>
            )}
            {groups.map((group) => (
              <div key={group.id} role="group" aria-labelledby={`language-group-${group.id}`} data-testid={`language-group-${group.id}`}>
                <div
                  id={`language-group-${group.id}`}
                  className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500"
                >
                  {t(group.labelKey)}
                </div>
                {group.languages.map((lang) => {
                  const selected = lang.code === current.code;
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      data-testid={`language-option-${lang.code}`}
                      aria-current={selected ? 'true' : undefined}
                      onClick={() => choose(lang.code)}
                      className={`w-full text-start px-4 py-2 text-sm flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700 ${
                        selected
                          ? 'bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-200 font-semibold'
                          : 'text-gray-700 dark:text-gray-200'
                      }`}
                    >
                      <span className="shrink-0 w-7 rounded bg-gray-100 dark:bg-gray-700 py-0.5 text-center text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">{lang.code}</span>
                      {/* <bdi> keeps "العربية" right-to-left inside an LTR list
                          (and Latin names LTR in an RTL one) while the row
                          keeps the list's alignment. */}
                      <span className="flex-1 min-w-0 truncate"><bdi lang={lang.code}>{lang.label}</bdi></span>
                      {lang.inProgress && (
                        <span
                          className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                          title={t('shell.languageSwitcher.inProgressHint')}
                        >
                          {t('shell.languageSwitcher.inProgress')}
                        </span>
                      )}
                      {selected && (
                        <svg className="shrink-0 w-4 h-4 text-primary-600 dark:text-primary-300" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
